import {
  ContentValidationError,
  parseDialogueCatalogJson,
  parseEntityDocumentJson,
  parseGameManifestJson,
  parseLevelDocumentJson,
  parseTileCatalogJson,
  parseWorldMapJson,
} from "./parser";
import { ContentRepository, type ContentDocumentLocations } from "./repository";
import type { EntityTypeId, GameContentDocuments, LevelId } from "./types";

export interface ContentSource {
  /** Manifest URL or reader key. Relative document paths resolve beside it. */
  readonly manifest?: string;
}

export type ContentReader = (filename: string) => string | Promise<string>;

const DEFAULT_MANIFEST = "content/game.json";

/** Loads authored content through fetch and validates the complete repository. */
export function loadContent(
  source: ContentSource = {},
): Promise<ContentRepository> {
  return loadContentWithReader(fetchText, source);
}

/** Loads authored content through an injected text reader for tests and tools. */
export async function loadContentWithReader(
  reader: ContentReader,
  source: ContentSource = {},
): Promise<ContentRepository> {
  const manifestFile = validateManifestLocation(
    source.manifest ?? DEFAULT_MANIFEST,
  );
  const manifest = parseGameManifestJson(
    await readText(reader, manifestFile),
    manifestFile,
  );
  const worldMapFile = resolveDocument(manifestFile, manifest.worldMap);
  const dialogueFile = resolveDocument(manifestFile, manifest.globalDialogues);
  const tilesFile = resolveDocument(manifestFile, manifest.tiles);

  const [worldMap, globalDialogues, tiles] = await Promise.all([
    readText(reader, worldMapFile).then((text) =>
      parseWorldMapJson(text, worldMapFile),
    ),
    readText(reader, dialogueFile).then((text) =>
      parseDialogueCatalogJson(text, dialogueFile),
    ),
    readText(reader, tilesFile).then((text) =>
      parseTileCatalogJson(text, tilesFile),
    ),
  ]);

  const levelEntries = [
    ...worldMap.nodes.map((node, index) => ({
      relative: node.level,
      ownerPath: `$.nodes[${index}].level`,
      expectedId: node.id,
    })),
    ...manifest.standaloneLevels.map((relative, index) => ({
      relative,
      ownerPath: `$.standaloneLevels[${index}]`,
      expectedId: null,
    })),
  ];
  const reservedPaths = new Set([
    manifest.worldMap,
    manifest.globalDialogues,
    manifest.tiles,
    ...manifest.entities,
  ]);
  const seenLevelFiles = new Map<string, string>();
  for (const entry of levelEntries) {
    if (reservedPaths.has(entry.relative)) {
      const ownerFile = entry.expectedId === null ? manifestFile : worldMapFile;
      throw new ContentValidationError(
        ownerFile,
        entry.ownerPath,
        `level path '${entry.relative}' is already used by a non-level document`,
      );
    }
    const previous = seenLevelFiles.get(entry.relative);
    if (previous !== undefined) {
      const ownerFile = entry.expectedId === null ? manifestFile : worldMapFile;
      throw new ContentValidationError(
        ownerFile,
        entry.ownerPath,
        `duplicates level document path already referenced at ${previous}`,
      );
    }
    seenLevelFiles.set(entry.relative, entry.ownerPath);
  }

  const entityResults = await Promise.all(
    manifest.entities.map(async (relative) => {
      const filename = resolveDocument(manifestFile, relative);
      return {
        filename,
        document: parseEntityDocumentJson(
          await readText(reader, filename),
          filename,
        ),
      };
    }),
  );
  const levelResults = await Promise.all(
    levelEntries.map(async (entry) => {
      const filename = resolveDocument(manifestFile, entry.relative);
      const document = parseLevelDocumentJson(
        await readText(reader, filename),
        filename,
      );
      if (entry.expectedId !== null && document.id !== entry.expectedId) {
        throw new ContentValidationError(
          filename,
          "$.id",
          `expected '${entry.expectedId}' because that is the referencing world-map node id`,
        );
      }
      return { filename, document };
    }),
  );

  const documents: GameContentDocuments = {
    manifest,
    worldMap,
    globalDialogues,
    tiles,
    entities: entityResults.map((result) => result.document),
    levels: levelResults.map((result) => result.document),
  };
  const entityLocations = new Map<EntityTypeId, string>();
  for (const result of entityResults)
    entityLocations.set(result.document.id, result.filename);
  const levelLocations = new Map<LevelId, string>();
  for (const result of levelResults)
    levelLocations.set(result.document.id, result.filename);
  const locations: ContentDocumentLocations = {
    manifest: manifestFile,
    worldMap: worldMapFile,
    globalDialogues: dialogueFile,
    tiles: tilesFile,
    entities: entityLocations,
    levels: levelLocations,
  };
  return new ContentRepository(documents, locations);
}

async function fetchText(filename: string): Promise<string> {
  const response = await fetch(filename);
  if (!response.ok)
    throw new Error(`${response.status} ${response.statusText}`);
  return response.text();
}

async function readText(
  reader: ContentReader,
  filename: string,
): Promise<string> {
  try {
    const result = await reader(filename);
    if (typeof result !== "string")
      throw new Error("reader returned a non-string value");
    return result;
  } catch (error) {
    if (error instanceof ContentValidationError) throw error;
    const detail = error instanceof Error ? error.message : String(error);
    throw new ContentValidationError(
      filename,
      "$",
      `unable to read document: ${detail}`,
    );
  }
}

function validateManifestLocation(value: string): string {
  if (value.length === 0 || value.includes("\0") || value.includes("\\")) {
    throw new ContentValidationError(
      value || "<manifest>",
      "$",
      "invalid manifest location",
    );
  }
  return value;
}

function resolveDocument(manifest: string, relative: string): string {
  if (/^[A-Za-z][A-Za-z\d+.-]*:/.test(manifest)) {
    try {
      return new URL(relative, new URL(".", manifest)).toString();
    } catch {
      throw new ContentValidationError(
        manifest,
        "$",
        `cannot resolve document path '${relative}'`,
      );
    }
  }
  const slash = manifest.lastIndexOf("/");
  return `${slash < 0 ? "" : manifest.slice(0, slash + 1)}${relative}`;
}
